import React, { useCallback, useEffect, useRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import Skeleton from 'react-loading-skeleton'

import LoadingSpinnerOverlay from 'shared/components/common/loading_spinner/LoadingSpinnerOverlay'
import UhOh, { getUhOhPropsForError } from 'shared/components/errors/UhOh'

import EmptyCart from 'public/components/default_template/online_ordering/cart/EmptyCart'
import { getCheckoutEligibleItems } from 'public/components/default_template/online_ordering/cart/cartUtils'
import CheckoutErrorModal from 'public/components/default_template/online_ordering/checkout/CheckoutErrorModal'
import { useCart } from 'public/components/online_ordering/CartContext'
import {
  CheckoutFormData,
  OrderError,
  useCheckout
} from 'public/components/online_ordering/CheckoutContext'
import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon'

import CheckoutSections from './CheckoutSections'
import { getCustomerInfo } from './checkoutUtils'
import { useSubmitOrder, useTrackPlaceOrderClicked } from './useSubmitOrder'
import { formatLocationAddress } from 'public/components/online_ordering/addressUtils'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { getDisplayBusinessName } from 'shared/js/locationUtils'
import {
  PaymentOption,
  usePayment
} from 'public/components/online_ordering/PaymentContext'
import { CreatePaymentMethodResultEvent } from 'public/components/online_ordering/types'
import { useSpiEventTracker } from 'public/components/default_template/online_ordering/checkout/payment/useSpi'
import { useCanSubmitOrder } from 'public/components/default_template/online_ordering/checkout/useCanSubmitOrder'
import { t } from 'src/translations/src'
import { useA11y } from 'src/public/components/online_ordering/useA11y'
import { useIsIntlRestaurant } from 'src/lib/js/hooks/useIsIntlRestaurant'

const CheckoutForm = () => {
  const submitOrder = useSubmitOrder()
  const { customer } = useCustomer()
  const { loadingCart, cart, error: cartError } = useCart()
  const { ooRestaurant, selectedLocation } = useRestaurant()
  const { createPaymentMethod, setPaymentMethod, paymentOption, paymentType } =
    usePayment()
  const {
    giftCardAppliedAmount,
    toastCashAppliedAmount,
    orderTotal,
    setReviewModalOpen,
    setOrderError
  } = useCheckout()
  const isIntlRestaurant = useIsIntlRestaurant()
  const isCartLoading = !cart && loadingCart
  const formMethods = useForm({
    mode: 'onTouched',
    defaultValues: getCustomerInfo(customer)
  })
  const restaurantName = getDisplayBusinessName(
    ooRestaurant?.whiteLabelName || cart?.restaurant?.name,
    selectedLocation?.name
  )
  const restaurantAddress = formatLocationAddress(cart?.restaurant?.location)
  const checkoutFormRef = useRef<HTMLDivElement>(null)
  const savedHeight = useRef<number>(500)
  const trackPlaceOrderClicked = useTrackPlaceOrderClicked()
  const { trackSpi } = useSpiEventTracker()
  const canSubmitOrder = useCanSubmitOrder()
  const { announce } = useA11y()

  const { reset, getValues, formState } = formMethods
  useEffect(() => {
    if (customer) {
      // Preserve curbside pickup data when customer logs in
      const currentValues = getValues() as Partial<CheckoutFormData>
      const customerInfo = getCustomerInfo(customer)

      // Merge customer info with preserved curbside data
      const updatedFormData: Partial<CheckoutFormData> = {
        ...customerInfo,
        curbsidePickup: currentValues.curbsidePickup || false,
        curbsidePickupVehicle: currentValues.curbsidePickupVehicle,
        curbsidePickupVehicleColor: currentValues.curbsidePickupVehicleColor
      }
      reset(updatedFormData)
    }
    // Note: formState.isValid isn't used in the effect, but is required in the dependency array
    // to ensure validation is running on initial load when customer is authenticated.
  }, [reset, getValues, customer, formState.isValid])

  // Capture and persist the form height when it's rendered (not loading)
  // This makes it less jarring on the customer when the form is loading
  useEffect(() => {
    if (!isCartLoading && checkoutFormRef.current) {
      const height = checkoutFormRef.current.clientHeight
      if (height > 0) {
        savedHeight.current = height
      }
    }
  }, [isCartLoading])

  const handleSubmit = useCallback(
    async (checkoutFormData: CheckoutFormData) => {
      if (isIntlRestaurant) {
        await submitOrder(checkoutFormData)
      } else {
        const canSubmit = canSubmitOrder()
        trackPlaceOrderClicked(canSubmit)
        if (!canSubmit) return
        // Match useSubmitOrder: no SPI when nothing is owed (e.g. gift card covers total).
        if (paymentOption === PaymentOption.PayNow && orderTotal > 0) {
          trackSpi('temp_SPI_completing_payment', { paymentType })
          const onSuccess = async (
            createPaymentEvent: CreatePaymentMethodResultEvent
          ) => {
            setPaymentMethod(createPaymentEvent.content)
            setReviewModalOpen(true)
          }
          await createPaymentMethod(
            giftCardAppliedAmount,
            toastCashAppliedAmount,
            onSuccess,
            (err: OrderError | null) => {
              if (err) setOrderError(err)
            }
          )
        } else {
          setReviewModalOpen(true)
        }
      }
    },
    [
      isIntlRestaurant,
      canSubmitOrder,
      trackPlaceOrderClicked,
      paymentOption,
      paymentType,
      orderTotal,
      trackSpi,
      createPaymentMethod,
      giftCardAppliedAmount,
      toastCashAppliedAmount,
      setPaymentMethod,
      setReviewModalOpen,
      setOrderError,
      submitOrder
    ]
  )

  useEffect(() => {
    if (formMethods.formState.isSubmitting) {
      announce(t('checkout-form.sr-only.placing-order'))
    }
  }, [announce, formMethods.formState.isSubmitting])

  const isEmptyCartError =
    cartError?.kind === 'CartError' &&
    cartError.message === 'Your cart is no longer available'
  if (cartError && !isEmptyCartError) {
    return <UhOh {...getUhOhPropsForError(cartError)} />
  }

  if (isCartLoading) {
    return (
      <div className='checkoutForm emptyCart'>
        <section className='checkoutSection currentSection'>
          <Skeleton width='100%' height={savedHeight.current} />
        </section>
      </div>
    )
  }

  if (!getCheckoutEligibleItems(cart)?.length || isEmptyCartError) {
    return <EmptyCart />
  }

  return (
    <div className='checkoutForm' ref={checkoutFormRef}>
      <div className='rxInfo'>
        <h2 className='rxName'>{restaurantName}</h2>
        <div className='rxAddress'>{restaurantAddress}</div>
      </div>
      <FormProvider {...formMethods}>
        <form
          className='checkoutFormContents'
          onSubmit={formMethods.handleSubmit(handleSubmit)}
        >
          {formMethods.formState.isSubmitting && (
            <LoadingSpinnerOverlay withBorderRadius={false} fullScreen={true} />
          )}
          <CheckoutSections />
        </form>
        <CheckoutErrorModal onSubmit={formMethods.handleSubmit(handleSubmit)} />
      </FormProvider>
    </div>
  )
}

export default CheckoutForm
